<script>
import {defineComponent, ref, reactive, computed, onBeforeMount} from "vue";
import MultiSelect from "primevue/multiselect";
import Button from "primevue/button";
import Calendar from "primevue/calendar";
import { useStore } from "vuex";
import { useI18n } from "vue-i18n";
import { useMeta } from "vue-meta";
import { downloadPayments, loadObjectsForPayments } from "@/services/payments";
import { getBookingIds } from "@/services/bookings";
import { useApiCall } from "@/composables/useApiCall";
import useDebounceCallback from "@/hooks/useDebounceCallback";

export default defineComponent({
  layout: {name: "AdminLayout"},
  components: {
    Button,
    Calendar,
    MultiSelect,
  },
  setup() {
    const store = useStore()
    const {t} = useI18n();
    useMeta({
      title: t('menu.payments')
    });

    const MESSAGES = {
      emptySelectionMessage: 'Не выбраны',
      emptyFilterMessage: 'Не найдено',
      emptyMessage: 'Список пуст'
    }

    const filters = reactive({
      dateFrom: null,
      dateTo: null,
      companyName: [],
      clients: [],
      bookingIds: null,
      roomNumber: [],
    })
    const companyNameOptions = [
      { name: 'ООО "УК "ОБВОДНЫЙ 118"' }, { name: 'ООО" ОТЕЛЬ ИЗМАЙЛОВСКИЙ 1"' }
    ]
    const usersOptions = computed(() => {
      const list = store.getters.getUsersList
      return !Array.isArray(list) ? list.data.data.map(usr => ({ id: usr.id, name: `${usr.last_name} ${usr.first_name}${usr.patronymic ? ' ' + usr.patronymic : ''}` })) : []
    });
    const bookingIdsOptions = computed(() => bookingIdsData.value?.data ? bookingIdsData.value?.data.filter(booking => booking.bitrix_deal_id) : []);
    const roomNumberOptions = computed(() => {
      const list = roomsData.value?.data
      return list ? list.map(room => ({ id: room.id, name: room.number })) : []
    })

    const isDataLoading = ref({
      clients: true,
      bookingIds: true,
      roomNumber: true,
    })
    const debouncedFiltering = useDebounceCallback()

    const { data: bookingIdsData, executeApiCall: getBookingIdsAction } = useApiCall(getBookingIds, true)
    const { data: roomsData, executeApiCall: getRoomsAction } = useApiCall(loadObjectsForPayments, true)
    const { data: paymentsData, isLoading: isPaymentsLoading, executeApiCall: downloadPaymentsAction } = useApiCall(downloadPayments, true)

    const onDownloadPayments = async () => {
      const body = {
        fromDate: filters.dateFrom ?new Date(filters.dateFrom).toLocaleDateString('ru-RU', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        }) : '',
        toDate: filters.dateTo ? new Date(filters.dateTo).toLocaleDateString('ru-RU', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        }) : '',
        company_name: filters.companyName.map(el => el.name).join(','),
        fio: filters.clients.map(el => el.name).join(','),
        booking: filters.bookingIds ? filters.bookingIds.map(el => el.bitrix_deal_id).filter(el => !!el).join(',') : '',
        room_number: filters.roomNumber.map(el => el.name).join(','),
      }
      try {
        await downloadPaymentsAction(body)
        const blob = paymentsData.value
        if (blob) {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.style.display = 'none'
          a.href = url;
          document.body.appendChild(a)
          a.download = 'payments.xlsx';
          a.click();
          URL.revokeObjectURL(url);
          document.body.removeChild(a)
        }
      } catch (error) {
        console.error(error)
      }
    }

    const fetchUsers = async (params = {}) => {
      await store.dispatch('fetchUsers', params)
    }

    const fetchRooms = async (params = {}) => {
      await getRoomsAction(params)
    }

    const handleFiltering = (key, obj) => {
      debouncedFiltering(async () => {
        if (key === 'clients') {
          isDataLoading.value.clients = true
  
          try {
            await fetchUsers({ fio: obj.value })
          } catch (error) {
            console.error(e)
          } finally {
            isDataLoading.value.clients = false
          }
        }
  
        // if (key === 'roomNumber') {
        //   isDataLoading.value.roomNumber = true
  
        //   try {
        //     await fetchRooms({ number: obj.value })
        //   } catch (error) {
        //     console.error(e)
        //   } finally {
        //     isDataLoading.value.roomNumber = false
        //   }
        // }
      })
    }

    const handleHide = async (key) => {
      if (key === 'clients') {
        isDataLoading.value.clients = true
  
        try {
          await fetchUsers()
        } catch (error) {
          console.error(e)
        } finally {
          isDataLoading.value.clients = false
        }
      }
      // if (key === 'roomNumber') {
      //   isDataLoading.value.roomNumber = true
  
      //   try {
      //     await fetchRooms({ page: 1 })
      //   } catch (error) {
      //     console.error(e)
      //   } finally {
      //     isDataLoading.value.roomNumber = false
      //   }
      // }
    }

    onBeforeMount(async () => {
      try {
        await fetchUsers()
        await getBookingIdsAction()
        await fetchRooms()
      } catch (error) {
        console.error(error);
      } finally {
        for (const key in isDataLoading.value) {
          isDataLoading.value[key] = false
        }
      }
    })

    return { t, MESSAGES, filters, isDataLoading, companyNameOptions, usersOptions, bookingIdsOptions, roomNumberOptions, isPaymentsLoading, handleFiltering, handleHide, onDownloadPayments };
  }
});
</script>

<template>
  <section class="py-2">
      <h1 class="mb-3">{{ t('menu.payments') }}</h1>
      <div class="p-card p-component p-3 mb-3 flex flex-column gap-3">
        <div class="flex flex-column gap-2">
          <div class="font-bold">Период</div>
          <div class="flex gap-2 align-items-center">
            <span class="p-float-label w-3">
              <Calendar 
              v-model="filters.dateFrom"
              date-format="dd.mm.yy"
              placeholder="Дата с"
              :max-date="filters.dateTo"
              showButtonBar
              class="w-full" />
              <i class="pi pi-calendar calendar-icon" />
            </span>
            -
            <span class="p-float-label w-3">
              <Calendar 
                v-model="filters.dateTo"
                date-format="dd.mm.yy"
                placeholder="Дата до"
                :min-date="filters.dateFrom"
                showButtonBar
                class="w-full" />
              <i class="pi pi-calendar calendar-icon" />
            </span>
          </div>
        </div>
        <div class="flex gap-3">
          <div class="flex flex-column gap-2 w-4">
            <div class="font-bold">Организация</div>
            <MultiSelect v-model="filters.companyName" :options="companyNameOptions" resetFilterOnHide v-bind="{...MESSAGES}" optionLabel="name" filter placeholder="Выберите организацию" class="w-full" />
          </div>
          <div class="flex flex-column gap-2 w-4">
            <div class="font-bold">ФИО клиента</div>
            <MultiSelect v-model="filters.clients" :loading="isDataLoading.clients" :options="usersOptions" resetFilterOnHide v-bind="{...MESSAGES}" optionLabel="name" filter placeholder="Выберите клиента" class="w-full" @filter="(obj) => handleFiltering('clients', obj)" @hide="handleHide('clients')">
              <template #value>
                <div class="h-full">{{ filters.clients.length > 0 ? filters.clients.map(el => el.name).join(', ') : 'Выберите клиента' }}</div>
              </template>
            </MultiSelect>
          </div>
        </div>
        <div class="flex gap-3">
          <div class="flex flex-column gap-2 w-4">
            <div class="font-bold">Номер бронирования</div>
            <MultiSelect v-model="filters.bookingIds" :loading="isDataLoading.bookingIds" :options="bookingIdsOptions" resetFilterOnHide v-bind="{...MESSAGES}" optionLabel="bitrix_deal_id" filter placeholder="Выберите номер бронирования" class="w-full" />
          </div>
          <div class="flex flex-column gap-2 w-4">
            <div class="font-bold">Номер помещения</div>
            <MultiSelect v-model="filters.roomNumber" :loading="isDataLoading.roomNumber" :options="roomNumberOptions" resetFilterOnHide v-bind="{...MESSAGES}" optionLabel="name" filter placeholder="Выберите номер помещения" class="w-full">
              <template #value>
                <div class="h-full">{{ filters.roomNumber.length > 0 ? filters.roomNumber.map(el => el.name).join(', ') : 'Выберите номер помещения' }}</div>
              </template>
            </MultiSelect>
          </div>
        </div>
      </div>
      <Button v-model="filters.companyName" :label="t('payments.download')" :disabled="isPaymentsLoading" disabled @click="onDownloadPayments" class="btn-primary font-light" />
  </section>

</template>

<style scoped>
.p-card {
  border-radius: 8px;
  box-shadow: none;
}
::v-deep(.calendar-icon) {
  position: absolute;
  top: 17px;
  right: 10px;
  pointer-events: none;
  cursor: pointer;
}
</style>